import {useCallback} from "react"
import {createUseMutation, UseMutationExtractor} from "@irati/utils/services/apollo"
import {pick} from "lodash"
import {BudgetCategories, MutationUpdateBudgetCategoriesArgs, UpdateBudgetCategoriesInput} from "@irati/types"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {gql} from "@irati/utils/gql"
import {AllBudgetCategoriesFragment} from "../../graph/fragments/budgetCategories"
import {useBudgetsPermissions} from "./useBudgetsPermissions"
import {budgetsCacheModify, useBudgetsList} from "./useBudgetsList"

const useBudgetsUpdateMutation = createUseMutation<BudgetCategories, MutationUpdateBudgetCategoriesArgs>(
  gql`
    mutation UpdateBudgetCategories($input: UpdateBudgetCategoriesInput!) {
      changeBudgetCategories(input: $input) {
        ...AllBudgetCategoriesFragment
      }
    }

    ${AllBudgetCategoriesFragment}
  `,
  {key: "changeBudgetCategories"}
)

type Extracted = UseMutationExtractor<typeof useBudgetsUpdateMutation>

export type UpdateBugets = Pick<UpdateBudgetCategoriesInput, "categories" | "newCategories">

type UseBudgetsUpdate = {
  update: (updates: UpdateBugets) => Promise<Extracted["mutationResult"] | void>
}

export function useBudgetsUpdate(): UseBudgetsUpdate {
  const uuid = useAuth().user?.uuid
  const {canBudget} = useBudgetsPermissions()
  const {data} = useBudgetsList()
  const [updateBudgets] = useBudgetsUpdateMutation({
    update(cache, {data: budgets}) {
      budgetsCacheModify({
        cache,
        variables: {uuid: uuid!},
        modify: ({write, cachedDate: wallet}) => write({...wallet, budgets: budgets!})
      })
    }
  })

  const categories = data?.budgets?.categories

  const update = useCallback<UseBudgetsUpdate["update"]>(
    async (updates) => {
      if (!canBudget || !categories) return
      await updateBudgets({
        variables: {
          input: {
            userId: uuid!,
            categories: updates.categories.map((c) => pick(c, ["amount", "color", "name", "tagId", "uuid"])),
            newCategories: updates.newCategories.map((c) => pick(c, ["amount", "color", "name", "tagId", "uuid"]))
          }
        }
      })
    },
    [updateBudgets, canBudget, categories, uuid]
  )

  return {update}
}
